import React, { useState, useEffect, useRef } from 'react';
import MapFrame from '../../components/MapFrame';
import BlockSubtitle from '../../components/BlockSubtitle';
import MusicPlayer from '../../components/MusicPlayer';
import './Rustico.css';

/* ── Helpers ──────────────────────────────────────────────────── */
const FILTER_CSS = {
  warm:     'sepia(0.3) saturate(1.3) brightness(1.05)',
  cool:     'hue-rotate(15deg) saturate(0.9) brightness(1.05)',
  vintage:  'sepia(0.5) contrast(0.9) brightness(1.1) saturate(0.8)',
  bw:       'grayscale(1)',
  dramatic: 'contrast(1.3) brightness(0.9) saturate(1.2)',
  soft:     'brightness(1.1) saturate(0.8) contrast(0.9)',
  golden:   'sepia(0.4) brightness(1.1) saturate(1.4) hue-rotate(-10deg)',
};
const flt = p => (p?.filter && p.filter !== 'none' ? FILTER_CSS[p.filter] : '') || undefined;
const objPos = p => (p && Number.isFinite(p.focalX) && Number.isFinite(p.focalY))
  ? `${(p.focalX * 100).toFixed(1)}% ${(p.focalY * 100).toFixed(1)}%`
  : undefined;
const src = u => !u ? null : u.startsWith('http') ? u : `${u}`;
const fmtDate = raw => {
  if (!raw) return 'ДД · ММ · ГГГГ';
  const d = new Date(raw);
  return [d.getDate(), d.getMonth() + 1, d.getFullYear()]
    .map(n => String(n).padStart(2, '0')).join(' · ');
};

/* ── SVG Decorations ──────────────────────────────────────────── */
const WheatLeft = () => (
  <svg className="rust-wheat rust-wheat--left" width="80" height="120" viewBox="0 0 80 120" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M40 110 Q30 80 20 50 Q10 20 15 5" stroke="#c17a54" strokeWidth="1.5" fill="none" strokeLinecap="round"/>
    <ellipse cx="15" cy="5" rx="6" ry="10" fill="#c17a54" opacity="0.7" transform="rotate(-20 15 5)"/>
    <ellipse cx="22" cy="20" rx="5" ry="9" fill="#c17a54" opacity="0.65" transform="rotate(-10 22 20)"/>
    <ellipse cx="18" cy="38" rx="5" ry="9" fill="#7a9e7e" opacity="0.65" transform="rotate(10 18 38)"/>
    <ellipse cx="25" cy="55" rx="5" ry="8" fill="#c17a54" opacity="0.6" transform="rotate(-5 25 55)"/>
    <ellipse cx="30" cy="70" rx="4" ry="7" fill="#7a9e7e" opacity="0.55" transform="rotate(15 30 70)"/>
    <path d="M15 5 Q5 15 8 28" stroke="#7a9e7e" strokeWidth="1" fill="none" strokeLinecap="round" opacity="0.7"/>
    <path d="M22 20 Q35 25 30 38" stroke="#7a9e7e" strokeWidth="1" fill="none" strokeLinecap="round" opacity="0.7"/>
  </svg>
);

const WheatRight = () => (
  <svg className="rust-wheat rust-wheat--right" width="80" height="120" viewBox="0 0 80 120" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M40 110 Q50 80 60 50 Q70 20 65 5" stroke="#c17a54" strokeWidth="1.5" fill="none" strokeLinecap="round"/>
    <ellipse cx="65" cy="5" rx="6" ry="10" fill="#c17a54" opacity="0.7" transform="rotate(20 65 5)"/>
    <ellipse cx="58" cy="20" rx="5" ry="9" fill="#c17a54" opacity="0.65" transform="rotate(10 58 20)"/>
    <ellipse cx="62" cy="38" rx="5" ry="9" fill="#7a9e7e" opacity="0.65" transform="rotate(-10 62 38)"/>
    <ellipse cx="55" cy="55" rx="5" ry="8" fill="#c17a54" opacity="0.6" transform="rotate(5 55 55)"/>
    <ellipse cx="50" cy="70" rx="4" ry="7" fill="#7a9e7e" opacity="0.55" transform="rotate(-15 50 70)"/>
    <path d="M65 5 Q75 15 72 28" stroke="#7a9e7e" strokeWidth="1" fill="none" strokeLinecap="round" opacity="0.7"/>
    <path d="M58 20 Q45 25 50 38" stroke="#7a9e7e" strokeWidth="1" fill="none" strokeLinecap="round" opacity="0.7"/>
  </svg>
);

const LeafDecor = ({ className = '' }) => (
  <svg className={`rust-leaf-decor ${className}`} width="60" height="30" viewBox="0 0 120 30" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M10 15 Q30 5 60 15 Q30 25 10 15Z" fill="#7a9e7e" opacity="0.5"/>
    <path d="M60 15 Q90 5 110 15 Q90 25 60 15Z" fill="#7a9e7e" opacity="0.5"/>
    <line x1="10" y1="15" x2="110" y2="15" stroke="#c17a54" strokeWidth="0.8" opacity="0.6"/>
    <circle cx="60" cy="15" r="3" fill="#c17a54" opacity="0.7"/>
  </svg>
);

const TwigDecor = () => (
  <svg className="rust-twig" width="120" height="40" viewBox="0 0 200 40" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M10 20 Q100 10 190 20" stroke="#c17a54" strokeWidth="1.2" fill="none" strokeLinecap="round" opacity="0.5"/>
    <path d="M40 20 Q50 8 55 5" stroke="#7a9e7e" strokeWidth="1" fill="none" strokeLinecap="round" opacity="0.6"/>
    <path d="M80 17 Q90 5 95 3" stroke="#7a9e7e" strokeWidth="1" fill="none" strokeLinecap="round" opacity="0.6"/>
    <path d="M120 18 Q130 6 135 4" stroke="#7a9e7e" strokeWidth="1" fill="none" strokeLinecap="round" opacity="0.6"/>
    <path d="M155 19 Q165 8 170 5" stroke="#7a9e7e" strokeWidth="1" fill="none" strokeLinecap="round" opacity="0.6"/>
    <ellipse cx="55" cy="5" rx="5" ry="3" fill="#7a9e7e" opacity="0.6" transform="rotate(-30 55 5)"/>
    <ellipse cx="95" cy="3" rx="5" ry="3" fill="#7a9e7e" opacity="0.6" transform="rotate(-25 95 3)"/>
    <ellipse cx="135" cy="4" rx="5" ry="3" fill="#7a9e7e" opacity="0.6" transform="rotate(-20 135 4)"/>
    <ellipse cx="170" cy="5" rx="5" ry="3" fill="#7a9e7e" opacity="0.6" transform="rotate(-15 170 5)"/>
  </svg>
);

const WavyDivider = ({ fill = '#f5ede0', className = '' }) => (
  <svg className={`rust-wave-divider ${className}`} viewBox="0 0 1440 60" xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="none">
    <path d="M0,30 C360,60 720,0 1080,30 C1260,45 1380,20 1440,30 L1440,60 L0,60 Z" fill={fill}/>
  </svg>
);

/* ── Countdown ──────────────────────────────────────────────────── */
function Countdown({ date, accent }) {
  const [t, setT] = useState({ d: 0, h: 0, m: 0, s: 0 });
  useEffect(() => {
    const tick = () => {
      const ms = Math.max(0, new Date(date) - Date.now());
      setT({ d: Math.floor(ms / 86400000), h: Math.floor(ms % 86400000 / 3600000),
             m: Math.floor(ms % 3600000 / 60000), s: Math.floor(ms % 60000 / 1000) });
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [date]);
  return (
    <div className="rust-countdown">
      {[['дней', t.d], ['часов', t.h], ['минут', t.m], ['секунд', t.s]].map(([u, v]) => (
        <div key={u} className="rust-cu">
          <span className="rust-cu-val" style={{ color: accent }}>{String(v).padStart(2, '0')}</span>
          <span className="rust-cu-label">{u}</span>
        </div>
      ))}
    </div>
  );
}

/* ── CalendarWidget ─────────────────────────────────────────────── */
function CalendarWidget({ date, accent, addBtn }) {
  if (!date) return null;
  const d = new Date(date), yr = d.getFullYear(), mo = d.getMonth(), day = d.getDate();
  const off = (new Date(yr, mo, 1).getDay() + 6) % 7;
  const dim = new Date(yr, mo + 1, 0).getDate();
  const prevDim = new Date(yr, mo, 0).getDate();
  const cells = [];
  for (let i = off - 1; i >= 0; i--) cells.push({ n: prevDim - i, cur: false });
  for (let i = 1; i <= dim; i++) cells.push({ n: i, cur: true });
  while (cells.length < 42) cells.push({ n: cells.length - off - dim + 1, cur: false });
  const MON = ['Январь','Февраль','Март','Апрель','Май','Июнь','Июль','Август','Сентябрь','Октябрь','Ноябрь','Декабрь'];
  return (
    <div className="rust-calendar">
      <div className="rust-cal-header">
        <span style={{ color: accent, fontWeight: 700 }}>{MON[mo]}</span>
        <span className="rust-cal-year">{yr}</span>
      </div>
      <div className="rust-cal-grid">
        {['Пн','Вт','Ср','Чт','Пт','Сб','Вс'].map(h => (
          <div key={h} className="rust-cal-head" style={{ color: accent }}>{h}</div>
        ))}
        {cells.map((c, i) => {
          const isDay = c.cur && c.n === day;
          return (
            <div key={i}
              className={`rust-cal-cell${!c.cur ? ' rust-cal-muted' : ''}${isDay ? ' rust-cal-today' : ''}`}
              style={isDay ? { background: accent, color: '#fffef9' } : {}}
            >{c.n}</div>
          );
        })}
      </div>
      {addBtn}
    </div>
  );
}

/* ── RSVPForm ───────────────────────────────────────────────────── */
function RSVPForm({ project, accent }) {
  const [status, setStatus] = useState('confirmed');
  const [name, setName] = useState('');
  const [survey, setSurvey] = useState({});
  const [message, setMessage] = useState('');
  const [done, setDone] = useState(false);
  const bc = project.block_content || {};

  if (done) return (
    <div className="rust-rsvp-done" style={{ borderColor: accent }}>
      <div className="rust-rsvp-done-icon">🌿</div>
      <strong style={{ color: accent }}>Спасибо!</strong>
      <p>{status === 'confirmed' ? 'Ждём вас с нетерпением!' : 'Жаль, что не сможете прийти.'}</p>
    </div>
  );

  return (
    <form className="rust-rsvp-form" onSubmit={e => { e.preventDefault(); setDone(true); }}>
      <div className="rust-rsvp-options">
        {[{ v: 'confirmed', l: bc.rsvp?.yesText || 'Приду!' }, { v: 'declined', l: bc.rsvp?.noText || 'Не смогу прийти' }].map(o => (
          <label key={o.v}
            className={`rust-rsvp-opt${status === o.v ? ' active' : ''}`}
            style={status === o.v ? { background: accent, color: '#fffef9', borderColor: accent } : {}}
          >
            <input type="radio" name="rsvp" value={o.v} checked={status === o.v}
              onChange={() => setStatus(o.v)} style={{ display: 'none' }} />
            {o.l}
          </label>
        ))}
      </div>

      <input className="rust-input" placeholder="Ваше имя"
        value={name} onChange={e => setName(e.target.value)} required />

      {project.blocks?.includes('survey') && status === 'confirmed' && project.survey?.map(q => (
        <div key={q.id} className="rust-survey-q">
          <p className="rust-survey-label">{q.question}</p>
          {q.type === 'text'
            ? <textarea className="rust-input" rows={2} value={survey[q.id] || ''}
                onChange={e => setSurvey(s => ({ ...s, [q.id]: e.target.value }))} />
            : q.type === 'multiple'
            ? <div className="rust-survey-opts">{(q.options || []).map(opt => {
                const selected = Array.isArray(survey[q.id]) ? survey[q.id].includes(opt) : false;
                return (
                  <label key={opt}
                    className={`rust-rsvp-opt rust-rsvp-opt-sm${selected ? ' active' : ''}`}
                    style={selected ? { background: accent, color: '#fffef9', borderColor: accent } : {}}
                  >
                    <input type="checkbox" name={q.id} value={opt} checked={selected}
                      onChange={e => {
                        const cur = Array.isArray(survey[q.id]) ? survey[q.id] : [];
                        setSurvey(s => ({ ...s, [q.id]: e.target.checked ? [...cur, opt] : cur.filter(v => v !== opt) }));
                      }} style={{ display: 'none' }} />
                    {opt}
                  </label>
                );
              })}</div>
            : <div className="rust-survey-opts">{(q.options || []).map(opt => (
                <label key={opt}
                  className={`rust-rsvp-opt rust-rsvp-opt-sm${survey[q.id] === opt ? ' active' : ''}`}
                  style={survey[q.id] === opt ? { background: accent, color: '#fffef9', borderColor: accent } : {}}
                >
                  <input type="radio" name={q.id} value={opt}
                    onChange={() => setSurvey(s => ({ ...s, [q.id]: opt }))} style={{ display: 'none' }} />
                  {opt}
                </label>
              ))}</div>
          }
        </div>
      ))}

      {project.blocks?.includes('message') && (
        <div>
          {bc.message?.fieldLabel && (
            <p style={{ fontSize: '0.82rem', color: '#9a8070', marginBottom: 4 }}>{bc.message.fieldLabel}</p>
          )}
          <textarea className="rust-input" rows={3}
            placeholder={bc.message?.text || 'Ваши пожелания молодым...'}
            value={message} onChange={e => setMessage(e.target.value)} />
        </div>
      )}

      <button type="submit" className="rust-btn" style={{ background: accent }}>
        {bc.rsvp?.btnText || 'Подтвердить'}
      </button>
    </form>
  );
}

/* ── Wishlist ───────────────────────────────────────────────────── */
function Wishlist({ project, accent }) {
  const [reserved, setReserved] = useState({});
  const items = project.wishlist || [];
  if (!items.length) return null;
  return (
    <div className="rust-wishlist">
      {items.map(item => (
        <div key={item.id} className="rust-wish-item">
          <div className="rust-wish-info">
            <div className="rust-wish-name">{item.name}</div>
            {item.price && <div className="rust-wish-price" style={{ color: accent }}>{item.price} ₽</div>}
          </div>
          <div className="rust-wish-actions">
            {item.link && <a href={item.link} target="_blank" rel="noopener noreferrer" className="rust-btn-outline">Посмотреть</a>}
            {reserved[item.id]
              ? <button type="button" className="rust-btn rust-btn-muted"
                  onClick={() => setReserved(r => ({ ...r, [item.id]: false }))}>
                  ✓ Снять резерв
                </button>
              : <button type="button" className="rust-btn" style={{ background: accent }}
                  onClick={() => setReserved(r => ({ ...r, [item.id]: true }))}>
                  Зарезервировать
                </button>
            }
          </div>
        </div>
      ))}
    </div>
  );
}

/* ── useReveal hook ─────────────────────────────────────────────── */
function useReveal(pageRef, blockKey) {
  useEffect(() => {
    const root = pageRef.current;
    if (!root) return;
    const SEL = '.rust-reveal';
    const inPreview = !!root.closest('.lp-template-inner, .lp-template-outer');
    if (inPreview) {
      root.querySelectorAll(SEL).forEach(el => el.classList.add('rust-in'));
      return;
    }
    const els = root.querySelectorAll(SEL);
    const obs = new IntersectionObserver(entries => {
      entries.forEach(e => {
        if (e.isIntersecting) { e.target.classList.add('rust-in'); obs.unobserve(e.target); }
      });
    }, { threshold: 0.1 });
    els.forEach(el => obs.observe(el));
    return () => obs.disconnect();
  }, [pageRef, blockKey]);
}

/* ══════════════════════════════════════════════════════════════════
   MAIN COMPONENT
══════════════════════════════════════════════════════════════════ */
export default function Rustico({ project, design, isPreview }) {
  const pageRef = useRef(null);
  const blocks = project.blocks || [];
  useReveal(pageRef, blocks.join(','));

  const bc = project.block_content || {};
  const accentRaw = project.palette?.primary || design?.config?.primaryColor || '#c17a54';
  const accent = accentRaw;

  const fontTitle = project.fonts?.title || design?.config?.fontTitle || null;
  const fontBody  = project.fonts?.body  || design?.config?.fontBody  || null;
  const fontStyle = {
    ...(fontTitle ? { '--f-disp': `'${fontTitle}', 'Merriweather', serif` } : {}),
    ...(fontBody  ? { '--f-body': `'${fontBody}', 'Lato', sans-serif` }    : {}),
  };

  // Dynamic Google Fonts
  useEffect(() => {
    const id = 'rustico-base-fonts';
    if (!document.getElementById(id)) {
      const link = document.createElement('link');
      link.id = id; link.rel = 'stylesheet';
      link.href = "https://fonts.googleapis.com/css2?family=Merriweather:ital,wght@0,300;0,400;0,700;1,300;1,400&family=Lato:wght@300;400;700&display=swap";
      document.head.appendChild(link);
    }
  }, []);

  useEffect(() => {
    const families = [fontTitle, fontBody].filter(Boolean);
    if (!families.length) return;
    const id = 'rustico-dynamic-fonts';
    let link = document.getElementById(id);
    if (!link) { link = document.createElement('link'); link.id = id; link.rel = 'stylesheet'; document.head.appendChild(link); }
    const query = families.map(f => `family=${encodeURIComponent(f)}:wght@400;600;700`).join('&');
    link.href = `https://fonts.googleapis.com/css2?${query}&display=swap`;
  }, [fontTitle, fontBody]);

  const photos    = project.cover_photos || (project.cover_photo ? [{ url: project.cover_photo }] : []);
  const invPhotos = project.invitation_photos || [];
  const invP1     = invPhotos[0] || photos[0] || null;
  const invP2     = invPhotos[1] || null;
  const dcPhotos  = project.dresscode_photos || [];
  const p1        = photos[0];

  const bride = project.bride || 'Невеста';
  const groom = project.groom || 'Жених';
  const venues = (project.venues || []).filter(v => (v.name || "").trim() || (v.address || "").trim());

  const tomorrowISO = (() => { const d = new Date(); d.setDate(d.getDate() + 1); return d.toISOString().slice(0, 10); })();
  const weddingDate = project.wedding_date || tomorrowISO;

  const HAS_MENU      = blocks.includes('menu');
  const HAS_CALENDAR  = blocks.includes('calendar');
  const HAS_COUNTDOWN = blocks.includes('countdown');
  const HAS_SCHEDULE  = blocks.includes('schedule');
  const HAS_MAP = blocks.includes('map');
  const HAS_DRESSCODE = blocks.includes('palette');
  const HAS_RSVP      = blocks.includes('rsvp');
  const HAS_WISHLIST  = blocks.includes('wishlist');

  const allItems  = project.schedule || [];
  const optional  = allItems.filter(i => i.optional);
  const mandatory = allItems.filter(i => !i.optional);
  const mainItems = optional.length > 0 ? mandatory : allItems;

  return (
    <div className="rust-page" ref={pageRef} style={fontStyle}>

      {/* ══ NAV ════════════════════════════════════════════════ */}
      {HAS_MENU && (
        <nav className="rust-nav">
          <span className="rust-nav-brand">{bride} &amp; {groom}</span>
          <div className="rust-nav-links">
            {HAS_SCHEDULE  && <a href="#schedule">Тайминг</a>}
            {HAS_MAP       && <a href="#map">Локация</a>}
            {HAS_DRESSCODE && <a href="#dresscode">Дресс-код</a>}
            {HAS_RSVP      && <a href="#rsvp">Анкета</a>}
          </div>
        </nav>
      )}

      {/* ══ HERO ═══════════════════════════════════════════════ */}
      <section className="rust-hero">
        {p1 && (
          <div className="rust-hero-photo">
            <img src={src(p1.url)} alt="" style={{ filter: flt(p1), objectPosition: objPos(p1) }} />
          </div>
        )}
        {p1 && <div className="rust-hero-overlay" />}
        <div className="rust-hero-content">
          <WheatLeft />
          <div className="rust-hero-center">
            <div className="rust-hero-slogan">
              {bc.hero?.subtitle || project.wedding_slogan || 'Вы приглашены'}
            </div>
            <h1 className="rust-hero-names">
              <em>{bride}</em>
              <span className="rust-hero-amp">&amp;</span>
              <em>{groom}</em>
            </h1>
            <LeafDecor className="rust-hero-leaf" />
            <div className="rust-hero-date">
              <span>{fmtDate(weddingDate)}</span>
            </div>
            {project.wedding_time && (
              <div className="rust-hero-time">{project.wedding_time}</div>
            )}
            {HAS_RSVP && (
              <a href="#rsvp" className="rust-btn rust-hero-btn" style={{ background: accent }}>
                {bc.rsvp?.yesText || 'Заполнить анкету'} →
              </a>
            )}
          </div>
          <WheatRight />
        </div>
        <WavyDivider fill="#f5ede0" className="rust-wave-bottom" />
      </section>

      {/* ══ INVITATION ══════════════════════════════════════════ */}
      <section className="rust-section rust-invitation" id="invitation">
        <div className="rust-invitation-left rust-reveal">
          <TwigDecor />
          <h2 className="rust-section-title"
            dangerouslySetInnerHTML={{ __html: bc.message?.title || 'Дорогие наши!' }} />
          <div className="rust-body-text">
            {bc.message?.text
              ? <div dangerouslySetInnerHTML={{ __html: bc.message.text }} />
              : (
                <>
                  <p>С огромным теплом и радостью мы хотим <strong>пригласить вас</strong> разделить с нами этот особенный день — день нашего <strong>бракосочетания</strong>.</p>
                  <p>Пусть этот праздник станет временем радости, смеха и добрых воспоминаний, которые мы будем хранить долгие годы.</p>
                  <p>Подтвердите, пожалуйста, своё присутствие <strong style={{ color: accent }}>через форму</strong> ниже.</p>
                </>
              )
            }
          </div>
          {HAS_RSVP && (
            <a href="#rsvp" className="rust-btn" style={{ background: accent, marginTop: 16 }}>
              {bc.rsvp?.btnText || 'Заполнить анкету'}
            </a>
          )}
        </div>

        <div className="rust-invitation-right rust-reveal" style={{ '--d': '.1s' }}>
          <div className="rust-photo-stack">
            {invP1 ? (
              <div className="rust-photo-frame rust-photo-frame--a">
                <img src={src(invP1.url)} alt="" style={{ filter: flt(invP1), objectPosition: objPos(invP1) }} />
              </div>
            ) : (
              <div className="rust-photo-frame rust-photo-frame--a rust-photo-frame--empty">
                <span>Ваше фото</span>
              </div>
            )}
            {invP2 ? (
              <div className="rust-photo-frame rust-photo-frame--b">
                <img src={src(invP2.url)} alt="" style={{ filter: flt(invP2), objectPosition: objPos(invP2) }} />
              </div>
            ) : (
              <div className="rust-photo-frame rust-photo-frame--b rust-photo-frame--empty">
                <span>Добавьте 2-е фото</span>
              </div>
            )}
          </div>

          <CalendarWidget
            date={weddingDate}
            accent={accent}
            addBtn={HAS_CALENDAR && (() => {
              const d = new Date(weddingDate);
              const pad = n => String(n).padStart(2, '0');
              const ymd = `${d.getFullYear()}${pad(d.getMonth() + 1)}${pad(d.getDate())}`;
              const title = encodeURIComponent(`Свадьба ${bride} & ${groom}`);
              const href = `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${title}&dates=${ymd}/${ymd}`;
              return (
                <a href={href} target="_blank" rel="noopener noreferrer"
                  className="rust-cal-add-btn" style={{ background: accent }}>
                  📅 {bc.calendar?.title || 'Добавить в календарь'}
                </a>
              );
            })()}
          />
        </div>
      </section>

      {/* ══ COUNTDOWN ═══════════════════════════════════════════ */}
      {HAS_COUNTDOWN && (
        <section className="rust-section rust-section--sand" id="countdown">
          <WavyDivider fill="#fffef9" className="rust-wave-top" />
          <div className="rust-section-inner">
            <LeafDecor />
            <h2 className="rust-section-title rust-reveal"
              dangerouslySetInnerHTML={{ __html: bc.countdown?.title || 'До нашего дня' }} />
            <BlockSubtitle text={bc.countdown?.subtitle} className="rust-section-sub rust-reveal" style={{ '--d': '.05s' }} />
            <div className="rust-reveal" style={{ '--d': '.1s' }}>
              <Countdown date={weddingDate} accent={accent} />
            </div>
          </div>
          <WavyDivider fill="#f5ede0" className="rust-wave-bottom" />
        </section>
      )}

      {/* ══ SCHEDULE ════════════════════════════════════════════ */}
      {HAS_SCHEDULE && (
        <section className="rust-section" id="schedule">
          <div className="rust-section-inner">
            <LeafDecor />
            <h2 className="rust-section-title rust-reveal"
              dangerouslySetInnerHTML={{ __html: bc.schedule?.title || 'Тайминг дня' }} />
            <BlockSubtitle text={bc.schedule?.subtitle} className="rust-section-sub rust-reveal" style={{ '--d': '.05s' }} />
            <div className="rust-timeline">
              {allItems.length > 0 ? (
                <>
                  {optional.length > 0 && (
                    <div className="rust-timeline-group">
                      <div className="rust-timeline-group-title">Необязательная часть</div>
                      {optional.map((item, i) => (
                        <div className="rust-timeline-item rust-reveal" key={i} style={{ '--d': `${i * 0.1}s` }}>
                          <div className="rust-timeline-dot" style={{ background: accent }} />
                          <div className="rust-timeline-time" style={{ color: accent }}>{item.time}</div>
                          <div className="rust-timeline-info">
                            <div className="rust-timeline-event">{item.event}</div>
                            {item.description && <div className="rust-timeline-desc">{item.description}</div>}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                  <div className="rust-timeline-group">
                    {optional.length > 0 && <div className="rust-timeline-group-title">Основная программа</div>}
                    {mainItems.map((item, i) => (
                      <div className="rust-timeline-item rust-reveal" key={i} style={{ '--d': `${i * 0.1}s` }}>
                        <div className="rust-timeline-dot" style={{ background: accent }} />
                        <div className="rust-timeline-time" style={{ color: accent }}>{item.time}</div>
                        <div className="rust-timeline-info">
                          <div className="rust-timeline-event">{item.event}</div>
                          {item.description && <div className="rust-timeline-desc">{item.description}</div>}
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              ) : (
                [['14:00','Сбор гостей'],['15:00','Церемония'],['16:00','Фотосессия'],['17:00','Банкет']].map(([t, e], i) => (
                  <div className="rust-timeline-item" key={i} style={{ opacity: 0.4 }}>
                    <div className="rust-timeline-dot" style={{ background: accent + '80' }} />
                    <div className="rust-timeline-time" style={{ color: accent + '80' }}>{t}</div>
                    <div className="rust-timeline-event">{e}</div>
                  </div>
                ))
              )}
            </div>
          </div>
        </section>
      )}

      {/* ══ MAP ═════════════════════════════════════════════════ */}
      {HAS_MAP && (
        <section className="rust-section rust-section--cream" id="map">
          <WavyDivider fill="#fffef9" className="rust-wave-top" />
          <div className="rust-section-inner">
            <LeafDecor />
            <h2 className="rust-section-title rust-reveal"
              dangerouslySetInnerHTML={{ __html: bc.map?.title || 'Место торжества' }} />
            <BlockSubtitle text={bc.map?.subtitle} className="rust-section-sub rust-reveal" style={{ '--d': '.05s' }} />
            <div className="rust-venues rust-reveal" style={{ '--d': '.1s' }}>
              {venues.length > 0 ? venues.map((v, i) => {
                const hasAddr = (v.address || '').trim();
                return (
                <div key={i} className="rust-venue-row">
                  {hasAddr && (
                  <div className="rust-venue-pin" style={{ color: accent }}>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill={accent}>
                      <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"/>
                    </svg>
                  </div>
                  )}
                  <div className="rust-venue-info">
                    {v.label && <span className="rust-venue-label" style={{ color: accent }}>{v.label}{(v.name || v.address) ? ' · ' : ''}</span>}
                    {v.name && <span className="rust-venue-name">{v.name}{v.address ? ' · ' : ''}</span>}
                    {hasAddr && <span>{v.address}</span>}
                  </div>
                  {hasAddr && (
                  <a href={`https://yandex.ru/maps/?text=${encodeURIComponent(v.address)}&z=16`}
                    target="_blank" rel="noopener noreferrer" className="rust-btn-outline">
                    Маршрут →
                  </a>
                  )}
                </div>
                );
              }) : (
                project.wedding_address ? (
                  <div className="rust-venue-row">
                    <div className="rust-venue-pin">
                      <svg width="18" height="18" viewBox="0 0 24 24" fill={accent}>
                        <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"/>
                      </svg>
                    </div>
                    <span className="rust-venue-info">{project.wedding_address}</span>
                  </div>
                ) : null
              )}
            </div>
            {(project.map_url || (project.wedding_address || '').trim() || venues.some(v => (v.address || '').trim())) && (
            <div className="rust-map-frame rust-reveal" style={{ '--d': '.2s' }}>
              {(() => {
                if (project.map_url) return <MapFrame region={project.region} title="Карта" src={project.map_url} allowFullScreen style={{ width: '100%', height: '100%', border: 'none' }} />;
                const withCoords = venues.filter(v => v.lat && v.lon);
                if (withCoords.length > 0) {
                  const clat = withCoords.reduce((s, v) => s + v.lat, 0) / withCoords.length;
                  const clon = withCoords.reduce((s, v) => s + v.lon, 0) / withCoords.length;
                  const pts  = withCoords.map(v => `${v.lon},${v.lat},pm2rdm`).join('~');
                  return <MapFrame region={project.region} title="Карта" src={`https://yandex.ru/map-widget/v1/?ll=${clon},${clat}&z=16&pt=${pts}&lang=ru_RU`} allowFullScreen style={{ width: '100%', height: '100%', border: 'none' }} allow="geolocation" />;
                }
                const addr = venues.map(v => v.address).filter(Boolean).join(' — ') || project.wedding_address || '';
                const mapSrc = addr
                  ? `https://yandex.ru/map-widget/v1/?text=${encodeURIComponent(addr)}&z=16&lang=ru_RU`
                  : `https://yandex.ru/map-widget/v1/?ll=37.617617,55.755811&z=12&lang=ru_RU`;
                return <MapFrame region={project.region} title="Карта" src={mapSrc} allowFullScreen style={{ width: '100%', height: '100%', border: 'none' }} allow="geolocation" />;
              })()}
            </div>
            )}
          </div>
          <WavyDivider fill="#f5ede0" className="rust-wave-bottom" />
        </section>
      )}

      {/* ══ DRESSCODE ═══════════════════════════════════════════ */}
      {HAS_DRESSCODE && (
        <section className="rust-section" id="dresscode">
          <div className="rust-section-inner">
            <LeafDecor />
            <h2 className="rust-section-title rust-reveal"
              dangerouslySetInnerHTML={{ __html: bc.palette?.title || 'Дресс-код' }} />
            <BlockSubtitle text={bc.palette?.subtitle} className="rust-body-text rust-reveal" tag="div" style={{ '--d': '.05s' }} />
            <div className="rust-swatches rust-reveal" style={{ '--d': '.1s' }}>
              {(project.dresscode_palette || []).map((sw, i) => {
                const c = sw.color || '#ffffff';
                const hex = c.replace('#', '');
                const r = parseInt(hex.slice(0, 2), 16) || 255, g = parseInt(hex.slice(2, 4), 16) || 255, b = parseInt(hex.slice(4, 6), 16) || 255;
                const isLight = (r * 299 + g * 587 + b * 114) / 1000 > 160;
                return (
                  <div key={i} className="rust-swatch"
                    style={{ background: c, border: isLight ? '1px solid rgba(193,122,84,0.3)' : 'none' }}>
                    <span style={{ color: isLight ? '#3d2b1f' : '#fffef9' }}>{sw.label}</span>
                  </div>
                );
              })}
            </div>
            {dcPhotos.length > 0 && (
              <div className="rust-dresscode-photos rust-reveal" style={{ '--d': '.15s' }}>
                {dcPhotos.map((p, i) => (
                  <div key={i} className="rust-dresscode-photo">
                    <img src={src(p.url)} alt="" style={{ filter: flt(p), objectPosition: objPos(p) }} />
                  </div>
                ))}
              </div>
            )}
          </div>
        </section>
      )}

      {/* ══ RSVP ════════════════════════════════════════════════ */}
      {HAS_RSVP && (
        <section className="rust-section rust-section--cream" id="rsvp">
          <WavyDivider fill="#fffef9" className="rust-wave-top" />
          <div className="rust-section-inner">
            <LeafDecor />
            <h2 className="rust-section-title rust-reveal"
              dangerouslySetInnerHTML={{ __html: bc.rsvp?.title || 'Подтверждение' }} />
            <BlockSubtitle text={bc.rsvp?.subtitle} className="rust-section-sub rust-reveal" style={{ '--d': '.05s' }} />
            <div className="rust-rsvp-wrap rust-reveal" style={{ '--d': '.1s' }}>
              <RSVPForm project={project} accent={accent} />
            </div>
          </div>
          <WavyDivider fill="#f5ede0" className="rust-wave-bottom" />
        </section>
      )}

      {/* ══ WISHLIST ════════════════════════════════════════════ */}
      {HAS_WISHLIST && (
        <section className="rust-section" id="wishlist">
          <div className="rust-section-inner">
            <LeafDecor />
            <h2 className="rust-section-title rust-reveal"
              dangerouslySetInnerHTML={{ __html: bc.wishlist?.title || 'Список желаний' }} />
            <BlockSubtitle text={bc.wishlist?.subtitle} className="rust-section-sub rust-reveal" style={{ '--d': '.05s' }} />
            <div className="rust-reveal" style={{ '--d': '.1s' }}>
              <Wishlist project={project} accent={accent} />
            </div>
          </div>
        </section>
      )}

      {/* ══ FOOTER ══════════════════════════════════════════════ */}
      <footer className="rust-footer">
        <WavyDivider fill="#3d2b1f" className="rust-wave-top" />
        <div className="rust-footer-inner">
          <TwigDecor />
          <div className="rust-footer-names">{bride} &amp; {groom}</div>
          <div className="rust-footer-date">{fmtDate(weddingDate)}</div>
          {project.wedding_slogan && <div className="rust-footer-slogan">{project.wedding_slogan}</div>}
        </div>
      </footer>

      {/* ══ WATERMARK ═══════════════════════════════════════════ */}
      {project.payment_plan === 'free' && (
        <div className="rust-watermark">
          Создано на <strong>WeddingInvite</strong>
        </div>
      )}

      {(project?.blocks || []).includes('music') && project?.music?.track?.url && (
        <MusicPlayer music={project.music} palette={project.palette} isPreview={isPreview} />
      )}
    </div>
  );
}
